import React, { useState } from 'react';
import PoweredByEaco from './PoweredByEaco';

interface PasswordViewProps {
  formName: string;
  /** What is being unlocked — the copy reads "This {noun} is password…". */
  noun?: 'form' | 'page';
  /** The previous attempt was wrong (server-verified). */
  invalid: boolean;
  /** A submitted attempt is being verified. */
  checking: boolean;
  onSubmit: (password: string) => void;
}

/**
 * Owner-password gate for a public form or a shared page. The server withholds
 * the content until a valid password accompanies the query, so this screen is
 * purely a front door — there is nothing to leak client-side.
 */
const PasswordView: React.FC<PasswordViewProps> = ({
  formName,
  noun = 'form',
  invalid,
  checking,
  onSubmit,
}) => {
  const [value, setValue] = useState('');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value.trim() !== '') onSubmit(value);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="flex flex-1 items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-4 text-center">
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold">
              {formName || `This ${noun}`}
            </h1>
            <p className="text-sm text-muted-foreground">
              This {noun} is password protected. Enter the password provided
              by its owner to continue.
            </p>
          </div>
          <input
            type="password"
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Password"
            aria-label={`${noun} password`}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
          />
          {invalid && !checking && (
            <p className="text-sm text-destructive">
              That password isn&rsquo;t right — please try again.
            </p>
          )}
          <button
            type="submit"
            disabled={checking || value.trim() === ''}
            className="w-full rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            {checking ? 'Checking…' : 'Continue'}
          </button>
        </form>
      </div>
      <PoweredByEaco />
    </div>
  );
};

export default PasswordView;
